import { useCallback, useMemo } from "react";
import { useCosmosApi } from "@/lib/api/useCosmosApi";
import { useAihrAuth } from "@/lib/auth/useAihrAuth";
import { cosmosApiUrl, isLocalCosmosApi, localCosmosHeaders } from "@/lib/cosmos-api-base-url";

export type CosmosQueryResponse = {
  conversationId?: string;
  assistantMessageId?: string;
  executionSnapshot?: {
    planId: string;
    intent: string;
    domains: string[];
    topicTerms?: string[];
    entities: Array<{
      id?: string;
      title: string;
      entityType?: string;
      description?: string;
      durationMinutes?: number;
      format?: string;
      status?: string;
      metadata?: Record<string, unknown>;
    }>;
  };
  runtimeHealth?: RuntimeHealthStatus;
  intentId?: string;
  semanticQuery: string;
  sql: string;
  rows: Array<
    Record<string, unknown> & {
      id?: string;
      title?: string;
      description?: string;
      slug?: string;
      url?: string;
      categories?: string[];
      difficulty?: string;
      durationMinutes?: number;
      ratingAverage?: number;
      ratingCount?: number;
    }
  >;
  queryPlan?: {
    intent?: string;
    domains?: string[];
    resultCardinality?: Record<string, unknown>;
    catalogTables?: Array<{
      columns?: Array<{
        name: string;
        enumMapping?: {
          values?: Array<{
            name?: string;
            storageValue?: unknown;
            numericValue?: number;
            aliases?: string[];
          }>;
        };
      }>;
    }>;
  };
  warnings?: string[];
  reasoningSummary?: string;
  executions?: Array<Record<string, unknown>>;
  queryExecution?: {
    engine: "seed" | "athena" | "postgres" | "meilisearch" | "kb" | "wordpress";
    queryExecutionId?: string;
    workgroup?: string;
    streamSource?: "bedrock" | "meilisearch";
    indexUid?: string;
    workspace?: string;
    estimatedTotalHits?: number;
  };
  modelRouting: {
    provider: string;
    model: string;
    rationale: string;
  };
  knowledgeContext: Array<{
    sourceId: string;
    sourceType: "course-content" | "internal-doc" | "blog-post" | "meilisearch";
    title: string;
    confidence: number;
    snippet?: string;
    uri?: string;
    metadata?: Record<string, unknown>;
  }>;
  answerProvenance?: {
    generatedAt: string;
    freshnessSummary?: string;
    verificationStatus: "verified" | "partial" | "unverified";
    sources: Array<{
      sourceId: string;
      displayName?: string;
      dialect?: string;
      lastRefreshedAt?: string;
      freshnessDescription?: string;
      rowCount?: number;
    }>;
    groundedClaims: Array<{
      claim: string;
      sourceIds: string[];
      confidence: number;
      supported: boolean;
    }>;
  };
  assistantReasoning?: string;
  presentationPlan?: {
    kind: "markdown" | "table" | "chart" | "cards" | "mixed";
    defaultTab: "summary" | "table" | "chart" | "sources";
    chartSpec?: {
      type: "bar" | "line" | "pie";
      xField: string;
      yField: string;
      title?: string;
    };
    columns?: string[];
    visibleRows?: number;
    primaryMetric?: { label: string; value: string | number };
    explanation?: string;
    confidence: number;
  };
  chatMode?: "learner" | "manager" | "operator";
  assistantResponse?: {
    text: string;
    mode: "live" | "fallback";
    provider: string;
    model: string;
    usage?: {
      promptTokens: number;
      completionTokens: number;
      estimatedUsdCost: number;
    };
  };
};

export type RuntimeHealthStatus = {
  mode: "live" | "degraded";
  degradedReasons: string[];
  configuredSources: string[];
  missingSources: string[];
};

export type CosmosConversation = {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  mode?: "query" | "recommend";
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  lastMessagePreview?: string;
};

export type CosmosConversationMessage = {
  id: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  model?: string;
  intentId?: string;
  recommendationSnapshot?: ConsultantRecommendationResponse;
  feedback?: "helpful" | "not_helpful";
  createdAt: string;
};

export type CosmosQueryMeta = Omit<CosmosQueryResponse, "assistantResponse">;

export type ConsultantEntityType =
  | "journey"
  | "course"
  | "lesson"
  | "module"
  | "event"
  | "blogPost"
  | "resource";

export type ConsultantCandidate = {
  entityType: ConsultantEntityType;
  id: string;
  title: string;
  description?: string;
  slug?: string;
  url?: string;
  domainTags: string[];
  categories?: string[];
  format?: string;
  difficulty?: string;
  durationMinutes?: number;
  credentialOffered?: boolean;
  practicality?: "low" | "medium" | "high";
  ratingAverage?: number;
  ratingCount?: number;
};

export type ConsultantRecommendationResponse = {
  recommendationId: string;
  extractedFilters?: {
    durationHoursPerLearner?: number;
    timeWindow?: string;
    urgency?: "normal" | "high";
    teamSize?: number;
    seniority?: string;
    difficulty?: "foundational" | "intermediate" | "advanced";
    domains: string[];
    formats: string[];
    priorKnowledge?: "none" | "basic" | "intermediate" | "advanced";
    credentialRequired?: boolean;
  };
  candidates: {
    journeys: ConsultantCandidate[];
    courses: ConsultantCandidate[];
    lessons: ConsultantCandidate[];
    modules: ConsultantCandidate[];
    events: ConsultantCandidate[];
    blogPosts: ConsultantCandidate[];
    resources: ConsultantCandidate[];
  };
  ranked: Array<{
    candidate: ConsultantCandidate;
    score: number;
    matchedSignals: string[];
  }>;
  journeyBlueprint: {
    title: string;
    summary: string;
    totalEstimatedMinutes: number;
    steps: Array<{
      phase: string;
      title: string;
      entityType: ConsultantEntityType;
      entityId?: string;
      durationMinutes?: number;
      why: string;
    }>;
  };
  chatAnswer: string;
  recommendationEngine?: "catalog" | "meilisearch-chat";
  sourceDocuments?: CosmosQueryResponse["knowledgeContext"];
  conversationId?: string;
  assistantMessageId?: string;
};

export type ConsultantFeedbackResponse = {
  status: "recorded";
  profile: {
    tenantId: string;
    featureWeights: Record<string, number>;
    feedbackCount: number;
    updatedAt: string;
  };
};

export type CosmosActionStatus =
  | "pending_approval"
  | "approved"
  | "rejected"
  | "completed"
  | "failed";

export type CosmosActionState = {
  requestId: string;
  actionType: string;
  payload: Record<string, unknown>;
  requestedBy: string;
  tenantId: string;
  reason?: string;
  status: CosmosActionStatus;
  approvalToken?: string;
  result?: unknown;
  error?: string;
  updatedAt: string;
};

export type CourseQuestionResponse = {
  course: ConsultantCandidate;
  answer: string;
  citations: CosmosQueryResponse["knowledgeContext"];
  modelRouting: {
    text: string;
    provider: string;
    model: string;
    mode: "live" | "fallback";
  };
};

export type CosmosRecommendStreamMeta = {
  recommendationEngine: "catalog" | "meilisearch-chat";
  extractedFilters: ConsultantRecommendationResponse["extractedFilters"];
  meilisearchChat?: {
    workspace?: string;
    index?: string;
    api: "chat-completions";
  };
};

export type CosmosRecommendStreamHandlers = {
  onMeta?: (meta: CosmosRecommendStreamMeta) => void;
  onStep?: (step: { label: string }) => void;
  onDelta?: (chunk: string) => void;
  onCandidate?: (candidate: ConsultantRecommendationResponse["ranked"][number]) => void;
  onDone?: (response: ConsultantRecommendationResponse) => void;
  onError?: (error: Error) => void;
};

export type CosmosStreamHandlers = {
  onMeta?: (meta: CosmosQueryMeta) => void;
  onStep?: (step: Record<string, unknown>) => void;
  onSource?: (source: CosmosQueryResponse["knowledgeContext"][number]) => void;
  onDelta?: (chunk: string) => void;
  onDone?: (response: CosmosQueryResponse) => void;
  onError?: (error: Error) => void;
};

export type CosmosStreamOptions = {
  conversationId?: string;
  conversationHistory?: Array<{
    role: "user" | "assistant";
    content: string;
    executionSnapshot?: CosmosQueryResponse["executionSnapshot"];
  }>;
  signal?: AbortSignal;
  dashboardMode?: boolean;
  reportMode?: boolean;
  journeyMode?: boolean;
};

export type CoursesResponse = {
  courses: ConsultantCandidate[];
  source: "postgres" | "seed";
};

export type CosmosRecentQuery = {
  prompt: string;
  semanticQuery: string;
  createdAt: string;
};

export type GlobalSearchCategory =
  | "All"
  | "Journeys"
  | "Courses"
  | "Lessons"
  | "Modules"
  | "Blog Posts"
  | "Resources Library"
  | "Licenses"
  | "Members";

export type GlobalSearchResultCategory = Exclude<GlobalSearchCategory, "All">;

export type GlobalSearchResultItem = {
  id: string;
  category: GlobalSearchResultCategory;
  title: string;
  subtitle: string;
  href: string;
  badge?: string;
  external?: boolean;
};

export type GlobalSearchResponse = {
  query: string;
  category: GlobalSearchCategory;
  index?: string;
  results: GlobalSearchResultItem[];
  grouped?: Partial<Record<GlobalSearchResultCategory, GlobalSearchResultItem[]>>;
  counts: Record<GlobalSearchResultCategory, number>;
  estimatedTotalHits: number;
};

export type RecentSearch = {
  query: string;
  category: GlobalSearchCategory;
  createdAt: string;
};

const consumeCosmosEventStream = async (
  response: Response,
  dispatch: (event: string, parsed: unknown) => void
): Promise<void> => {
  if (!response.body) {
    throw new Error("Cosmos stream returned an empty response body.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const dispatchFrame = (frame: string) => {
    const event = frame
      .split("\n")
      .find((line) => line.startsWith("event: "))
      ?.slice("event: ".length);
    const data = frame
      .split("\n")
      .find((line) => line.startsWith("data: "))
      ?.slice("data: ".length);
    if (!event || !data) {
      return;
    }
    dispatch(event, JSON.parse(data) as unknown);
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    let separatorIndex = buffer.indexOf("\n\n");
    while (separatorIndex >= 0) {
      const frame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);
      if (frame.trim()) {
        dispatchFrame(frame);
      }
      separatorIndex = buffer.indexOf("\n\n");
    }
    if (done) {
      if (buffer.trim()) {
        dispatchFrame(buffer);
      }
      break;
    }
  }
};

export function useCosmosClient() {
  const request = useCosmosApi();
  const { authEnabled, getAccessToken } = useAihrAuth();

  const streamQuery = useCallback(
    async (
      prompt: string,
      handlers: CosmosStreamHandlers,
      options: CosmosStreamOptions = {}
    ): Promise<void> => {
      const token = authEnabled ? await getAccessToken() : undefined;
      const headers = new Headers();
      headers.set("accept", "text/event-stream");
      headers.set("content-type", "application/json");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      } else {
        if (!isLocalCosmosApi()) {
          throw new Error(
            "Auth0 is not configured for this Cosmos app environment."
          );
        }
        for (const [key, value] of Object.entries(localCosmosHeaders())) {
          headers.set(key, String(value));
        }
      }

      try {
        const response = await fetch(cosmosApiUrl("/api/query/stream"), {
          method: "POST",
          headers,
          body: JSON.stringify({
            prompt,
            conversationId: options.conversationId,
            conversationHistory: options.conversationHistory,
            dashboardMode: options.dashboardMode,
            reportMode: options.reportMode,
            journeyMode: options.journeyMode
          }),
          signal: options.signal
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Cosmos stream failed (${response.status}): ${text}`);
        }

        await consumeCosmosEventStream(response, (event, parsed) => {
          if (event === "meta") {
            handlers.onMeta?.(parsed as CosmosQueryMeta);
          } else if (event === "step") {
            handlers.onStep?.(parsed as Record<string, unknown>);
          } else if (event === "source") {
            handlers.onSource?.(
              parsed as CosmosQueryResponse["knowledgeContext"][number]
            );
          } else if (event === "delta") {
            handlers.onDelta?.((parsed as { text: string }).text);
          } else if (event === "done") {
            handlers.onDone?.(parsed as CosmosQueryResponse);
          } else if (event === "error") {
            throw new Error(
              (parsed as { message?: string }).message ?? "Stream error"
            );
          }
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const normalized =
          error instanceof Error
            ? error
            : new Error("Unknown Cosmos stream error");
        handlers.onError?.(normalized);
        throw normalized;
      }
    },
    [authEnabled, getAccessToken]
  );

  const streamRecommendContent = useCallback(
    async (
      prompt: string,
      maxResults: number,
      handlers: CosmosRecommendStreamHandlers,
      options: { signal?: AbortSignal; conversationId?: string } = {}
    ): Promise<void> => {
      const token = authEnabled ? await getAccessToken() : undefined;
      const headers = new Headers();
      headers.set("accept", "text/event-stream");
      headers.set("content-type", "application/json");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      } else {
        if (!isLocalCosmosApi()) {
          throw new Error(
            "Auth0 is not configured for this Cosmos app environment."
          );
        }
        for (const [key, value] of Object.entries(localCosmosHeaders())) {
          headers.set(key, String(value));
        }
      }

      try {
        const response = await fetch(cosmosApiUrl("/api/consultant/recommend/stream"), {
          method: "POST",
          headers,
          body: JSON.stringify({
            prompt,
            maxResults,
            ...(options.conversationId ? { conversationId: options.conversationId } : {}),
          }),
          signal: options.signal
        });

        if (!response.ok) {
          const text = await response.text();
          throw new Error(`Cosmos recommend stream failed (${response.status}): ${text}`);
        }

        await consumeCosmosEventStream(response, (event, parsed) => {
          if (event === "meta") {
            handlers.onMeta?.(parsed as CosmosRecommendStreamMeta);
          } else if (event === "step") {
            handlers.onStep?.(parsed as { label: string });
          } else if (event === "delta") {
            handlers.onDelta?.((parsed as { text: string }).text);
          } else if (event === "candidate") {
            handlers.onCandidate?.(
              parsed as ConsultantRecommendationResponse["ranked"][number]
            );
          } else if (event === "done") {
            handlers.onDone?.(parsed as ConsultantRecommendationResponse);
          } else if (event === "error") {
            throw new Error(
              (parsed as { message?: string }).message ?? "Stream error"
            );
          }
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const normalized =
          error instanceof Error
            ? error
            : new Error("Unknown Cosmos recommend stream error");
        handlers.onError?.(normalized);
        throw normalized;
      }
    },
    [authEnabled, getAccessToken]
  );

  return useMemo(
    () => ({
      runQuery: (prompt: string) =>
        request<CosmosQueryResponse>("/api/query", {
          method: "POST",
          body: JSON.stringify({ prompt })
        }),
      getRuntimeHealth: () =>
        request<RuntimeHealthStatus>("/api/runtime/health"),
      listConversations: (params: { search?: string; limit?: number } = {}) => {
        const search = new URLSearchParams();
        if (params.search) search.set("search", params.search);
        if (params.limit) search.set("limit", String(params.limit));
        const suffix = search.size > 0 ? `?${search.toString()}` : "";
        return request<CosmosConversation[]>(`/api/conversations${suffix}`);
      },
      createConversation: (title?: string) =>
        request<CosmosConversation>("/api/conversations", {
          method: "POST",
          body: JSON.stringify(title ? { title } : {})
        }),
      getConversationMessages: (
        conversationId: string,
        params: { limit?: number; before?: string } = {}
      ) => {
        const search = new URLSearchParams();
        if (params.limit) search.set("limit", String(params.limit));
        if (params.before) search.set("before", params.before);
        const suffix = search.size > 0 ? `?${search.toString()}` : "";
        return request<CosmosConversationMessage[]>(
          `/api/conversations/${encodeURIComponent(conversationId)}/messages${suffix}`
        );
      },
      renameConversation: (conversationId: string, title: string) =>
        request<CosmosConversation>(
          `/api/conversations/${encodeURIComponent(conversationId)}`,
          {
            method: "PATCH",
            body: JSON.stringify({ title })
          }
        ),
      deleteConversation: (conversationId: string) =>
        request<{ deleted: boolean }>(
          `/api/conversations/${encodeURIComponent(conversationId)}`,
          { method: "DELETE" }
        ),
      submitAnswerFeedback: (body: {
        conversationId: string;
        messageId: string;
        verdict: "helpful" | "not_helpful";
        notes?: string;
        feedbackContext?: {
          queryExecution?: CosmosQueryResponse["queryExecution"];
          recommendationId?: string;
          recommendationEngine?: ConsultantRecommendationResponse["recommendationEngine"];
          sourceTitles?: string[];
        };
      }) =>
        request<{ status: "recorded"; messageId: string; verdict: string }>(
          "/api/query/feedback",
          {
            method: "POST",
            body: JSON.stringify(body)
          }
        ),
      listRecentQueries: (limit = 10) =>
        request<CosmosRecentQuery[]>(`/api/query/recent?limit=${limit}`),
      search: (params: {
        query: string;
        category?: GlobalSearchCategory;
        limit?: number;
        offset?: number;
        signal?: AbortSignal;
      }) => {
        const search = new URLSearchParams();
        search.set("query", params.query);
        if (params.category) search.set("category", params.category);
        if (params.limit) search.set("limit", String(params.limit));
        if (params.offset) search.set("offset", String(params.offset));
        return request<GlobalSearchResponse>(`/api/search?${search.toString()}`, {
          signal: params.signal
        });
      },
      listRecentSearches: (limit = 8) =>
        request<RecentSearch[]>(`/api/search/recent?limit=${limit}`),
      recordRecentSearch: (body: {
        query: string;
        category?: GlobalSearchCategory;
      }) =>
        request<RecentSearch>("/api/search/recent", {
          method: "POST",
          body: JSON.stringify(body)
        }),
      recommendContent: (prompt: string, maxResults = 20) =>
        request<ConsultantRecommendationResponse>("/api/consultant/recommend", {
          method: "POST",
          body: JSON.stringify({ prompt, maxResults })
        }),
      streamRecommendContent,
      submitRecommendationFeedback: (body: {
        recommendationId: string;
        verdict: "good" | "bad";
        improvementAreas?: string[];
        notes?: string;
      }) =>
        request<ConsultantFeedbackResponse>("/api/consultant/feedback", {
          method: "POST",
          body: JSON.stringify(body)
        }),
      streamQuery,
      listCourses: (params: {
        query?: string;
        domain?: string;
        difficulty?: string;
        limit?: number;
      } = {}) => {
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (value !== undefined && value !== "") {
            search.set(key, String(value));
          }
        }
        const suffix = search.size > 0 ? `?${search.toString()}` : "";
        return request<CoursesResponse>(`/api/courses${suffix}`);
      },
      getCourse: (courseId: string) =>
        request<ConsultantCandidate>(`/api/courses/${encodeURIComponent(courseId)}`),
      askCourse: (
        courseId: string,
        body: {
          prompt: string;
          history?: Array<{ role: "user" | "assistant"; content: string }>;
        }
      ) =>
        request<CourseQuestionResponse>(
          `/api/courses/${encodeURIComponent(courseId)}/ask`,
          {
            method: "POST",
            body: JSON.stringify(body)
          }
        ),
      requestAction: (body: {
        actionType: string;
        payload: Record<string, unknown>;
        reason?: string;
      }) =>
        request<CosmosActionState>("/api/actions/request", {
          method: "POST",
          body: JSON.stringify(body)
        }),
      listActions: (status?: CosmosActionStatus) =>
        request<CosmosActionState[]>(
          status ? `/api/actions?status=${status}` : "/api/actions"
        ),
      decideAction: (requestId: string, body: {
        decision: "approve" | "reject";
        approvalToken: string;
        reason?: string;
      }) =>
        request<CosmosActionState>(`/api/actions/${requestId}/decision`, {
          method: "POST",
          body: JSON.stringify(body)
        }),
      actionStatus: (requestId: string) =>
        request<CosmosActionState>(`/api/actions/${requestId}/status`),
      adapters: () => request<Array<Record<string, unknown>>>("/api/adapters"),
      observability: () => request<Record<string, unknown>>("/api/observability/summary"),
      answerQuality: () => request<Record<string, unknown>>("/api/observability/quality"),
      telemetry: () => request<Record<string, unknown>>("/api/observability/telemetry"),
      memory: () => request<Array<Record<string, unknown>>>("/api/memory/export")
    }),
    [request, streamQuery, streamRecommendContent]
  );
}
